import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LoginForm, RegisterForm, ForgotForm, ResetForm } from "@/components/AuthForms";
import { useAuth } from "@/context/AuthContext";
import { Truck, Heart, Wallet, Receipt, ShieldCheck, Lightning, ArrowLeft } from "@phosphor-icons/react";

const GoogleG = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const BENEFITS = [
  { icon: Truck, title: "Sigue tus pedidos", desc: "Rastreo en vivo de cada envío" },
  { icon: Heart, title: "Guarda favoritos", desc: "Y te avisamos cuando se reabastecen" },
  { icon: Wallet, title: "Saldo a favor", desc: "Úsalo al pagar, automáticamente" },
  { icon: Receipt, title: "Facturación", desc: "Solicita y descarga tus facturas" },
];

export default function AuthScreen() {
  const { login } = useAuth();
  const [params, setParams] = useSearchParams();
  const resetToken = params.get("reset");
  const [mode, setMode] = useState("login");
  const clearReset = () => { setParams({}); setMode("login"); };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row bg-background" data-testid="auth-screen">
      {/* Brand panel */}
      <div className="relative md:w-1/2 lg:w-[55%] overflow-hidden bg-[hsl(var(--brand-navy))] text-white px-6 py-8 md:px-12 md:py-14 flex flex-col">
        <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[hsl(var(--brand-orange))]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-[hsl(var(--brand-orange))]/10 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />

        <Link to="/" data-testid="auth-back-home" className="relative inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors">
          <ArrowLeft weight="bold" size={16} /> Volver a la tienda
        </Link>

        <div className="relative flex-1 flex flex-col justify-center py-6 auth-in">
          <img src="/todito-logo.png" alt="Todito Ventas" className="hidden md:block h-14 w-auto mb-6 bg-white rounded-xl px-4 py-2 self-start nb-shadow" />
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[hsl(var(--brand-orange))] text-white text-xs font-bold px-3 py-1 mb-4">
            <Lightning weight="fill" size={13} /> Tu cuenta Todito
          </span>
          <h1 className="font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
            Compra fácil,<br />rápido y seguro.
          </h1>
          <p className="text-white/70 mt-4 max-w-md text-base">
            Crea tu cuenta o inicia sesión para seguir tus pedidos, guardar favoritos y gestionar tus compras en un solo lugar.
          </p>

          <div className="hidden md:grid grid-cols-2 gap-3 mt-8 max-w-lg">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="flex items-start gap-3 rounded-xl bg-white/[0.06] border border-white/10 p-3 backdrop-blur-sm auth-in"
                     style={{ animationDelay: `${120 + i * 90}ms` }}>
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-[hsl(var(--brand-orange))]/90 flex items-center justify-center">
                    <Icon weight="fill" size={18} className="text-white" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold leading-tight">{b.title}</span>
                    <span className="block text-xs text-white/60 leading-tight mt-0.5">{b.desc}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative text-white/40 text-xs hidden md:block">© {new Date().getFullYear()} Todito Ventas · Hecho en México</div>
      </div>

      {/* Auth card */}
      <div className="md:w-1/2 lg:w-[45%] flex items-center justify-center px-6 py-12 md:py-8">
        <div className="w-full max-w-sm auth-in" style={{ animationDelay: "90ms" }}>
          <img src="/todito-logo.png" alt="Todito Ventas" className="h-14 w-auto mx-auto mb-6 md:hidden" />

          {resetToken ? <ResetForm token={resetToken} onDone={clearReset} /> : mode === "forgot" ? <ForgotForm onBack={() => setMode("login")} /> : (<>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-center">{mode === "login" ? "Bienvenido" : "Crea tu cuenta"}</h2>
          <p className="text-muted-foreground text-center mt-2 text-sm">{mode === "login" ? "Ingresa con tu correo o con Google." : "Regístrate en segundos con tu correo o con Google."}</p>

          <div className="mt-6 grid grid-cols-2 p-1 rounded-full bg-black/[0.05]" role="tablist">
            {[["login", "Ingresar"], ["register", "Crear cuenta"]].map(([k, l]) => (
              <button key={k} onClick={() => setMode(k)} data-testid={`auth-tab-${k}`} role="tab" aria-selected={mode === k}
                className={`rounded-full py-2 text-sm font-bold transition-[background-color,color,box-shadow] ${mode === k ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>

          <div className="mt-5">{mode === "login" ? <LoginForm onForgot={() => setMode("forgot")} /> : <RegisterForm />}</div>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-black/10" />
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold">o</span>
            <span className="h-px flex-1 bg-black/10" />
          </div>

          <button onClick={login} data-testid="google-login-button"
                  className="group w-full flex items-center justify-center gap-3 rounded-full bg-white border border-black/15 py-3.5 font-semibold text-foreground nb-shadow hover:nb-shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200">
            <GoogleG size={22} />
            <span>Continuar con Google</span>
          </button>

          <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck weight="fill" size={15} className="text-[hsl(var(--meli-green))]" />
            Conexión segura · Tus datos están protegidos
          </div>
          </>)}

          <div className="my-7 flex items-center gap-3 md:hidden">
            <span className="h-px flex-1 bg-black/10" />
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold">Tu cuenta incluye</span>
            <span className="h-px flex-1 bg-black/10" />
          </div>

          <ul className="space-y-2.5 md:hidden">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <li key={b.title} className="flex items-center gap-3 text-sm">
                  <span className="shrink-0 w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
                    <Icon weight="bold" size={15} className="text-primary" />
                  </span>
                  <span><b className="font-semibold">{b.title}.</b> <span className="text-muted-foreground">{b.desc}</span></span>
                </li>
              );
            })}
          </ul>

          <p className="text-[11px] text-muted-foreground text-center mt-8 leading-relaxed">
            Al continuar aceptas nuestros Términos y nuestra Política de privacidad.
          </p>
        </div>
      </div>
    </div>
  );
}
